import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { AlertCircle, User, Phone, Mail, Calendar, CheckCircle2, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const CompleteProfileModal = ({ open, onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileStatus, setProfileStatus] = useState(null);
  const [formData, setFormData] = useState({
    user_name: '',
    user_phone: '',
    volunteer_name: '',
    volunteer_phone: '',
    volunteer_email: '',
    volunteer_cpf: '',
    volunteer_birth_date: ''
  });

  useEffect(() => {
    if (open) {
      checkProfileStatus();
    }
  }, [open]);

  const checkProfileStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/auth/profile-status`);
      const data = response.data;
      
      setProfileStatus(data);
      
      // Pre-fill form with existing data
      setFormData({
        user_name: data.user?.name || '',
        user_phone: data.user?.phone || '',
        volunteer_name: data.volunteer?.name || '',
        volunteer_phone: data.volunteer?.phone || '',
        volunteer_email: data.volunteer?.email || '',
        volunteer_cpf: data.volunteer?.cpf || '',
        volunteer_birth_date: data.volunteer?.birth_date || ''
      });
      
      // If profile is complete, notify parent
      if (data.is_complete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error checking profile status:', error);
      toast.error('Erro ao verificar status do perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const missingUser = profileStatus?.user_missing_fields || [];
    const missingVolunteer = profileStatus?.volunteer_missing_fields || [];
    
    // User validations
    if (missingUser.includes('name') && !formData.user_name) {
      toast.error('Por favor, preencha seu nome');
      return;
    }
    if (missingUser.includes('phone') && !formData.user_phone) {
      toast.error('Por favor, preencha seu telefone');
      return;
    }
    
    // Volunteer validations
    if (missingVolunteer.includes('name') && !formData.volunteer_name) {
      toast.error('Por favor, preencha o nome do voluntário');
      return;
    }
    if (missingVolunteer.includes('phone') && !formData.volunteer_phone) {
      toast.error('Por favor, preencha o telefone do voluntário');
      return;
    }
    if (missingVolunteer.includes('email') && !formData.volunteer_email) {
      toast.error('Por favor, preencha o email do voluntário');
      return;
    }
    if (missingVolunteer.includes('cpf') && !formData.volunteer_cpf) {
      toast.error('Por favor, preencha o CPF do voluntário');
      return;
    }
    if (missingVolunteer.includes('birth_date') && !formData.volunteer_birth_date) {
      toast.error('Por favor, preencha a data de nascimento');
      return;
    }

    try {
      setSaving(true);
      await axios.put(`${API}/auth/complete-profile`, formData);
      toast.success('Perfil atualizado com sucesso!');
      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const formatCPF = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  };

  if (loading) {
    return (
      <Dialog open={open}>
        <DialogContent className="max-w-md">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!profileStatus || profileStatus.is_complete) {
    return null;
  }

  const userMissing = profileStatus.user_missing_fields || [];
  const volunteerMissing = profileStatus.volunteer_missing_fields || [];
  const hasVolunteer = !!profileStatus.volunteer;

  // Field labels in Portuguese
  const fieldLabels = {
    name: 'Nome Completo',
    phone: 'Telefone',
    email: 'Email',
    cpf: 'CPF',
    birth_date: 'Data de Nascimento'
  };

  return (
    <Dialog open={open}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertCircle size={24} />
            Complete seu Cadastro
          </DialogTitle>
          <DialogDescription>
            Para usar o sistema, precisamos que você preencha algumas informações importantes.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto pr-2 space-y-6">
          {/* User Section */}
          {userMissing.length > 0 && (
            <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                <User size={18} />
                Dados do Usuário
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {userMissing.includes('name') && (
                  <div>
                    <Label className="flex items-center gap-1 text-sm">
                      <User size={14} />
                      Nome Completo *
                    </Label>
                    <Input
                      value={formData.user_name}
                      onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                      placeholder="Seu nome completo"
                      required
                    />
                  </div>
                )}
                
                {userMissing.includes('phone') && (
                  <div>
                    <Label className="flex items-center gap-1 text-sm">
                      <Phone size={14} />
                      Telefone *
                    </Label>
                    <Input
                      value={formData.user_phone}
                      onChange={(e) => setFormData({ ...formData, user_phone: formatPhone(e.target.value) })}
                      placeholder="(00) 00000-0000"
                      required
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Volunteer Section */}
          {hasVolunteer && volunteerMissing.length > 0 && (
            <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 flex items-center gap-2">
                <CheckCircle2 size={18} />
                Dados do Voluntário
                <span className="text-xs font-normal text-green-600 bg-green-100 px-2 py-0.5 rounded">
                  {profileStatus.volunteer?.name || 'Vinculado'}
                </span>
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {volunteerMissing.includes('name') && (
                  <div>
                    <Label className="flex items-center gap-1 text-sm">
                      <User size={14} />
                      Nome Completo *
                    </Label>
                    <Input
                      value={formData.volunteer_name}
                      onChange={(e) => setFormData({ ...formData, volunteer_name: e.target.value })}
                      placeholder="Nome do voluntário"
                      required
                    />
                  </div>
                )}
                
                {volunteerMissing.includes('phone') && (
                  <div>
                    <Label className="flex items-center gap-1 text-sm">
                      <Phone size={14} />
                      Telefone *
                    </Label>
                    <Input
                      value={formData.volunteer_phone}
                      onChange={(e) => setFormData({ ...formData, volunteer_phone: formatPhone(e.target.value) })}
                      placeholder="(00) 00000-0000"
                      required
                    />
                  </div>
                )}
                
                {volunteerMissing.includes('email') && (
                  <div>
                    <Label className="flex items-center gap-1 text-sm">
                      <Mail size={14} />
                      Email *
                    </Label>
                    <Input
                      type="email"
                      value={formData.volunteer_email}
                      onChange={(e) => setFormData({ ...formData, volunteer_email: e.target.value })}
                      placeholder="email@exemplo.com"
                      required
                    />
                  </div>
                )}
                
                {volunteerMissing.includes('cpf') && (
                  <div>
                    <Label className="flex items-center gap-1 text-sm">
                      <CreditCard size={14} />
                      CPF *
                    </Label>
                    <Input
                      value={formData.volunteer_cpf}
                      onChange={(e) => setFormData({ ...formData, volunteer_cpf: formatCPF(e.target.value) })}
                      placeholder="000.000.000-00"
                      maxLength={14}
                      required
                    />
                  </div>
                )}
                
                {volunteerMissing.includes('birth_date') && (
                  <div>
                    <Label className="flex items-center gap-1 text-sm">
                      <Calendar size={14} />
                      Data de Nascimento *
                    </Label>
                    <Input
                      type="date"
                      value={formData.volunteer_birth_date}
                      onChange={(e) => setFormData({ ...formData, volunteer_birth_date: e.target.value })}
                      required
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              <strong>Importante:</strong> Esses dados são necessários para o funcionamento correto do sistema.
              Você só precisará preencher uma vez.
            </p>
          </div>

          <div className="flex justify-end pb-2">
            <Button type="submit" disabled={saving} className="btn-primary">
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle2 size={18} className="mr-2" />
                  Salvar e Continuar
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CompleteProfileModal;
